'use client'

import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        destructive:
          'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline:
          'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline'
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  iconPosition = 'left',
  loading = false,
  children,
  disabled,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    iconPosition?: 'left' | 'right'
    loading?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  // Handle icon positioning
  const childrenArray = React.Children.toArray(children)

  // Improved icon detection to handle both direct SVG elements and icon components (like those from lucide-react)
  const isIconElement = (child: React.ReactNode): boolean => {
    if (!React.isValidElement(child)) {
      return false
    }

    // Check if it's an SVG element directly
    if ('type' in child && (child.type === 'svg' || (typeof child.type === 'string' && child.type.includes('svg')))) {
      return true
    }

    // Check if it's a component that likely renders an SVG (like lucide-react icons)
    // This checks for components with names like "Sun", "Moon", "Languages", etc.
    return (
      'type' in child &&
      typeof child.type === 'function' &&
      'props' in child &&
      child.props !== null &&
      typeof child.props === 'object' &&
      'className' in child.props &&
      typeof child.props.className === 'string' &&
      (child.props.className.includes('h-') || child.props.className.includes('w-'))
    )
  }

  const hasIcon = childrenArray.some(isIconElement)

  // Reorder children based on iconPosition if there's an icon
  const orderedChildren =
    hasIcon && iconPosition === 'right'
      ? [
          ...childrenArray.filter(child => !isIconElement(child)),
          ...childrenArray.filter(child => isIconElement(child))
        ]
      : children

  // When asChild is true, we need to ensure we only pass a single child to Slot
  if (asChild) {
    // Find the first valid element to clone and pass props to
    const firstValidChild = React.Children.toArray(orderedChildren).find(child => React.isValidElement(child)) as
      | React.ReactElement<{
          className?: string
          disabled?: boolean
          children?: React.ReactNode
        }>
      | undefined

    if (!firstValidChild) {
      throw new Error('Button with asChild prop must have at least one valid child element')
    }

    // Clone the child and add our props
    return React.cloneElement(firstValidChild, {
      ...props,
      className: cn(buttonVariants({ variant, size, className }), firstValidChild.props.className),
      disabled: loading || disabled || firstValidChild.props.disabled,
      children: (
        <>
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {firstValidChild.props.children}
        </>
      )
    })
  }

  // Regular button rendering
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      disabled={loading || disabled}
      {...props}>
      {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
      {orderedChildren}
    </Comp>
  )
}

export { Button, buttonVariants }
